import { useEffect, useId, useRef, useState, useCallback } from 'react'
import { motion, AnimatePresence } from 'motion/react'
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, X } from 'lucide-react'
import {
  format,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
  addMonths,
  subMonths,
  addYears,
  subYears,
  startOfWeek,
  endOfWeek,
  parseISO,
  isValid,
  addDays,
  subDays,
} from 'date-fns'
import { TOKENS } from './dashboard-primitives'

interface AccessibleCalendarProps {
  isOpen: boolean
  onClose: () => void
  selectedDate: string
  onSelectDate: (iso: string) => void
  title?: string
  helper?: string
  defaultMonth?: Date
  /** Restore focus to this element on close. Pass the trigger button. */
  returnFocusRef?: React.RefObject<HTMLElement | null>
  /** Optional set of ISO 'yyyy-MM-dd' strings that show an availability dot. */
  availableDates?: Set<string>
  /** When true, days NOT in availableDates are visually dimmed and not selectable. */
  disableUnavailable?: boolean
}

const DOW = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const

/**
 * Accessible date picker:
 * - role="dialog", aria-modal, aria-labelledby, return-focus
 * - keyboard: arrows move focus by day, PageUp/Down by month, Shift+PageUp/Down by year, Home/End by week, Enter/Space selects, Esc closes
 * - focus trap inside the dialog
 * - day buttons have aria-label with full formatted date
 */
export function AccessibleCalendar({
  isOpen,
  onClose,
  selectedDate,
  onSelectDate,
  title = 'Choose a date',
  helper,
  defaultMonth,
  returnFocusRef,
  availableDates,
  disableUnavailable = false,
}: AccessibleCalendarProps) {
  const baseId = useId()
  const titleId = `${baseId}-title`
  const gridId = `${baseId}-grid`

  const parsedSelected = selectedDate && isValid(parseISO(selectedDate)) ? parseISO(selectedDate) : null
  const initialMonth = parsedSelected ?? defaultMonth ?? new Date()
  const initialFocus = parsedSelected ?? defaultMonth ?? new Date()

  const [currentMonth, setCurrentMonth] = useState(initialMonth)
  const [focused, setFocused] = useState<Date>(initialFocus)
  const dialogRef = useRef<HTMLDivElement>(null)
  const dayRefs = useRef<Record<string, HTMLButtonElement | null>>({})
  const titleRef = useRef<HTMLHeadingElement>(null)
  const previouslyFocused = useRef<HTMLElement | null>(null)

  // Reset when opening. This effect legitimately syncs to the open transition:
  // it initializes month/focus to the current selection AND captures the
  // previously focused element (a DOM read that must stay in an effect), so the
  // in-effect setState guard is intentionally disabled here.
  useEffect(() => {
    if (isOpen) {
      const parsed = selectedDate && isValid(parseISO(selectedDate)) ? parseISO(selectedDate) : null
      const month = parsed ?? defaultMonth ?? new Date()
      /* eslint-disable react-hooks/set-state-in-effect */
      setCurrentMonth(month)
      setFocused(parsed ?? defaultMonth ?? new Date())
      /* eslint-enable react-hooks/set-state-in-effect */
      previouslyFocused.current = (document.activeElement as HTMLElement) ?? null
    }
  }, [isOpen, selectedDate, defaultMonth])

  // Focus the focused day after open and after focus changes
  useEffect(() => {
    if (!isOpen) return
    const key = format(focused, 'yyyy-MM-dd')
    const el = dayRefs.current[key]
    if (el) {
      el.focus()
    } else if (titleRef.current) {
      titleRef.current.focus()
    }
  }, [isOpen, focused])

  // Restore focus on close
  useEffect(() => {
    if (!isOpen) {
      const target = returnFocusRef?.current ?? previouslyFocused.current
      if (target) {
        // Defer to next frame so the dialog is fully unmounted
        const id = requestAnimationFrame(() => target.focus())
        return () => cancelAnimationFrame(id)
      }
    }
  }, [isOpen, returnFocusRef])

  // Lock body scroll while open
  useEffect(() => {
    if (!isOpen) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [isOpen])

  const moveFocus = useCallback((nextDate: Date) => {
    if (!isSameMonth(nextDate, currentMonth)) {
      setCurrentMonth(startOfMonth(nextDate))
    }
    setFocused(nextDate)
  }, [currentMonth])

  const isDayDisabled = useCallback(
    (day: Date) => {
      if (!disableUnavailable || !availableDates) return false
      return !availableDates.has(format(day, 'yyyy-MM-dd'))
    },
    [disableUnavailable, availableDates],
  )

  const select = useCallback(
    (day: Date) => {
      if (isDayDisabled(day)) return
      onSelectDate(format(day, 'yyyy-MM-dd'))
      onClose()
    },
    [onSelectDate, onClose, isDayDisabled],
  )

  const onGridKey = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      const handlers: Record<string, () => void> = {
        ArrowLeft: () => moveFocus(subDays(focused, 1)),
        ArrowRight: () => moveFocus(addDays(focused, 1)),
        ArrowUp: () => moveFocus(subDays(focused, 7)),
        ArrowDown: () => moveFocus(addDays(focused, 7)),
        Home: () => moveFocus(startOfWeek(focused, { weekStartsOn: 1 })),
        End: () => moveFocus(endOfWeek(focused, { weekStartsOn: 1 })),
        PageUp: () => moveFocus(e.shiftKey ? subYears(focused, 1) : subMonths(focused, 1)),
        PageDown: () => moveFocus(e.shiftKey ? addYears(focused, 1) : addMonths(focused, 1)),
        Enter: () => select(focused),
        ' ': () => select(focused),
      }
      const h = handlers[e.key]
      if (h) {
        e.preventDefault()
        // The grid owns these keys while it is open. Let one out and a page
        // listening on `window` acts on it too, behind the scrim.
        e.stopPropagation()
        h()
      }
    },
    [focused, moveFocus, select],
  )

  // Trap focus + Escape
  useEffect(() => {
    if (!isOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        onClose()
      } else if (e.key === 'Tab') {
        const dialog = dialogRef.current
        if (!dialog) return
        const focusables = dialog.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        )
        if (focusables.length === 0) return
        const first = focusables[0]
        const last = focusables[focusables.length - 1]
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault()
          last.focus()
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault()
          first.focus()
        }
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [isOpen, onClose])

  const monthStart = startOfMonth(currentMonth)
  const monthEnd = endOfMonth(currentMonth)
  const days = eachDayOfInterval({
    start: startOfWeek(monthStart, { weekStartsOn: 1 }),
    end: endOfWeek(monthEnd, { weekStartsOn: 1 }),
  })

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            onClick={onClose}
            aria-hidden
            className="fixed inset-0"
            style={{ backgroundColor: 'rgba(28, 24, 22, 0.62)', zIndex: 60 }}
          />
          <div
            className="fixed inset-0 flex items-center justify-center p-4 pointer-events-none"
            role="presentation"
            style={{ zIndex: 61 }}
          >
            <motion.div
              ref={dialogRef}
              role="dialog"
              aria-modal="true"
              aria-labelledby={titleId}
              initial={{ opacity: 0, y: 16, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 8, scale: 0.98 }}
              transition={{ type: 'spring', stiffness: 320, damping: 28 }}
              className="pointer-events-auto w-full max-w-md rounded-2xl overflow-hidden"
              style={{
                backgroundColor: 'rgba(34, 28, 24, 0.96)',
                // eslint-disable-next-line no-restricted-syntax -- pre-existing color-literal drift, tracked in docs/refactor-suggestions.md Phase 0/2
                border: '1px solid rgba(245, 237, 225, 0.12)',
                boxShadow: '0 30px 80px -20px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.04)',
              }}
            >
              {/* Header */}
              <div className="flex items-start justify-between gap-4 px-6 pt-6 pb-4">
                <div className="min-w-0">
                  <h2
                    ref={titleRef}
                    id={titleId}
                    tabIndex={-1}
                    className="text-lg leading-tight outline-none"
                    style={{ fontFamily: 'var(--font-display)', fontWeight: 500, color: TOKENS.cream }}
                  >
                    {title}
                  </h2>
                  {helper && (
                    <p
                      className="mt-1 text-[10px] tracking-[0.28em] uppercase"
                      style={{ fontFamily: 'var(--font-mono)', color: TOKENS.creamDim }}
                    >
                      {helper}
                    </p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  aria-label="Close calendar"
                  className="inline-flex items-center justify-center rounded-lg min-h-[44px] min-w-[44px] transition-colors outline-none focus-visible:ring-2"
                  style={
                    {
                      color: TOKENS.creamDim,
                      // eslint-disable-next-line no-restricted-syntax -- pre-existing color-literal drift, tracked in docs/refactor-suggestions.md Phase 0/2
                      ['--tw-ring-color' as never]: 'rgba(232, 197, 160, 0.55)',
                    } as React.CSSProperties
                  }
                  onMouseEnter={(e) => (e.currentTarget.style.color = TOKENS.cream)}
                  onMouseLeave={(e) => (e.currentTarget.style.color = TOKENS.creamDim)}
                >
                  <X className="w-5 h-5" strokeWidth={2} />
                </button>
              </div>

              {/* Month navigation */}
              <div
                className="flex items-center justify-between px-4 py-2"
                // eslint-disable-next-line no-restricted-syntax -- pre-existing color-literal drift, tracked in docs/refactor-suggestions.md Phase 0/2
                style={{ borderTop: '1px solid rgba(245, 237, 225, 0.08)', borderBottom: '1px solid rgba(245, 237, 225, 0.08)' }}
              >
                <div className="inline-flex gap-1">
                  <NavBtn label="Previous year" onClick={() => setCurrentMonth((m) => subYears(m, 1))}>
                    <ChevronsLeft className="w-4 h-4" strokeWidth={2} />
                  </NavBtn>
                  <NavBtn label="Previous month" onClick={() => setCurrentMonth((m) => subMonths(m, 1))}>
                    <ChevronLeft className="w-4 h-4" strokeWidth={2} />
                  </NavBtn>
                </div>
                <span
                  className="text-xs tracking-[0.24em] uppercase tabular-nums"
                  style={{ fontFamily: 'var(--font-mono)', color: TOKENS.cream }}
                  aria-live="polite"
                >
                  {format(currentMonth, 'MMMM yyyy')}
                </span>
                <div className="inline-flex gap-1">
                  <NavBtn label="Next month" onClick={() => setCurrentMonth((m) => addMonths(m, 1))}>
                    <ChevronRight className="w-4 h-4" strokeWidth={2} />
                  </NavBtn>
                  <NavBtn label="Next year" onClick={() => setCurrentMonth((m) => addYears(m, 1))}>
                    <ChevronsRight className="w-4 h-4" strokeWidth={2} />
                  </NavBtn>
                </div>
              </div>

              {/* Calendar grid */}
              <div className="px-4 py-5">
                <div className="grid grid-cols-7 gap-1 mb-1.5">
                  {DOW.map((d) => (
                    <span
                      key={d}
                      className="text-center text-[10px] tracking-[0.22em] uppercase py-1"
                      style={{ fontFamily: 'var(--font-mono)', color: TOKENS.creamFaint }}
                      aria-hidden
                    >
                      {d}
                    </span>
                  ))}
                </div>
                <div
                  id={gridId}
                  role="grid"
                  aria-labelledby={titleId}
                  className="grid grid-cols-7 gap-1"
                  onKeyDown={onGridKey}
                >
                  {days.map((day) => {
                    const inMonth = isSameMonth(day, currentMonth)
                    const isSelected = parsedSelected ? isSameDay(day, parsedSelected) : false
                    const isFocused = isSameDay(day, focused)
                    const key = format(day, 'yyyy-MM-dd')
                    const isAvailable = availableDates ? availableDates.has(key) : false
                    const disabled = isDayDisabled(day)
                    return (
                      <button
                        key={key}
                        ref={(el) => {
                          dayRefs.current[key] = el
                        }}
                        type="button"
                        role="gridcell"
                        aria-selected={isSelected}
                        aria-label={
                          format(day, 'EEEE, MMMM d, yyyy') +
                          (availableDates ? (isAvailable ? ', has session' : ', no session') : '')
                        }
                        aria-disabled={disabled || undefined}
                        tabIndex={isFocused ? 0 : -1}
                        onClick={() => select(day)}
                        className="relative aspect-square rounded-lg text-sm tabular-nums transition-colors outline-none focus-visible:ring-2 min-h-[40px]"
                        style={
                          {
                            backgroundColor: isSelected ? TOKENS.amber : 'transparent',
                            color: isSelected
                              ? '#1c1816'
                              : disabled
                                ? TOKENS.creamFaint
                                : inMonth
                                  ? TOKENS.cream
                                  : TOKENS.creamFaint,
                            opacity: disabled ? 0.4 : 1,
                            cursor: disabled ? 'not-allowed' : 'pointer',
                            fontFamily: 'var(--font-mono)',
                            fontWeight: isSelected ? 600 : 400,
                            // eslint-disable-next-line no-restricted-syntax -- pre-existing color-literal drift, tracked in docs/refactor-suggestions.md Phase 0/2
                            ['--tw-ring-color' as never]: 'rgba(232, 197, 160, 0.55)',
                          } as React.CSSProperties
                        }
                        onMouseEnter={(e) => {
                          // eslint-disable-next-line no-restricted-syntax -- pre-existing color-literal drift, tracked in docs/refactor-suggestions.md Phase 0/2
                          if (!isSelected) e.currentTarget.style.backgroundColor = 'rgba(232, 197, 160, 0.1)'
                        }}
                        onMouseLeave={(e) => {
                          if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent'
                        }}
                      >
                        {format(day, 'd')}
                        {availableDates && isAvailable && !isSelected && inMonth && (
                          <span
                            aria-hidden
                            className="absolute left-1/2 -translate-x-1/2"
                            style={{
                              bottom: 5,
                              width: 4,
                              height: 4,
                              borderRadius: 999,
                              backgroundColor: TOKENS.amber,
                              // eslint-disable-next-line no-restricted-syntax -- pre-existing color-literal drift, tracked in docs/refactor-suggestions.md Phase 0/2
                              boxShadow: '0 0 6px rgba(232, 197, 160, 0.5)',
                            }}
                          />
                        )}
                      </button>
                    )
                  })}
                </div>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  )
}

function NavBtn({ label, onClick, children }: { label: string; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className="inline-flex items-center justify-center rounded-lg min-h-[44px] min-w-[44px] transition-colors outline-none focus-visible:ring-2"
      style={
        {
          color: TOKENS.creamDim,
          // eslint-disable-next-line no-restricted-syntax -- pre-existing color-literal drift, tracked in docs/refactor-suggestions.md Phase 0/2
          ['--tw-ring-color' as never]: 'rgba(232, 197, 160, 0.55)',
        } as React.CSSProperties
      }
      onMouseEnter={(e) => {
        e.currentTarget.style.color = TOKENS.cream
        // eslint-disable-next-line no-restricted-syntax -- pre-existing color-literal drift, tracked in docs/refactor-suggestions.md Phase 0/2
        e.currentTarget.style.backgroundColor = 'rgba(245, 237, 225, 0.06)'
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.color = TOKENS.creamDim
        e.currentTarget.style.backgroundColor = 'transparent'
      }}
    >
      {children}
    </button>
  )
}
